/**
 * Helper functions for working with CalendarProvider enum
 * The enum values from the backend are GOOGLE and MICROSOFT (uppercase)
 */

import { CalendarProvider } from '../../generated/graphql';

/**
 * Constants for provider strings used in URL state parameters
 * These are lowercase strings for URL compatibility, derived from CalendarProvider enum
 */
export const PROVIDER_STRING_GOOGLE = CalendarProvider.Google.toLowerCase();
export const PROVIDER_STRING_MICROSOFT =
  CalendarProvider.Microsoft.toLowerCase();

/**
 * Converts a lowercase provider string to the CalendarProvider enum value
 * @param provider - Lowercase provider string ('google' or 'microsoft')
 * @returns CalendarProvider enum value
 */
export const toCalendarProvider = (
  provider: string | null | undefined,
): CalendarProvider => {
  if (provider?.toLowerCase() === PROVIDER_STRING_MICROSOFT) {
    return CalendarProvider.Microsoft;
  }
  return CalendarProvider.Google; // Default to GOOGLE
};

/**
 * Checks if a provider string or enum value represents Microsoft
 * Handles both enum values ('MICROSOFT') and lowercase strings ('microsoft')
 * @param provider - Provider string or enum value
 * @returns true if provider is Microsoft
 */
export const isMicrosoftProvider = (
  provider: string | null | undefined,
): boolean => {
  if (!provider) return false;
  return (
    provider === CalendarProvider.Microsoft ||
    provider.toUpperCase() === CalendarProvider.Microsoft
  );
};

/**
 * Checks if a provider string or enum value represents Google
 * Handles both enum values ('GOOGLE') and lowercase strings ('google')
 * @param provider - Provider string or enum value
 * @returns true if provider is Google
 */
export const isGoogleProvider = (
  provider: string | null | undefined,
): boolean => {
  if (!provider) return false;
  return (
    provider === CalendarProvider.Google ||
    provider.toUpperCase() === CalendarProvider.Google
  );
};
